// app/layout.tsx
"use client";
import { ReactQueryProvider } from "./providers/ReactQueryProvider";
import "./globals.css";
import { ZustandProvider } from "./providers/ZustandProvider";
import { OpenAPIProvider } from "./providers/OpenAPIProvider";
import { GoogleTagManager } from "@next/third-parties/google";
import { usePathname } from "next/navigation";
import Script from "next/script";

const nativeWebViewBottomOffsetScript = `
(function () {
  function applyBottomOffset() {
    document.documentElement.style.setProperty(
      "--app-native-tab-bar-height",
      "0px"
    );
  }
  applyBottomOffset();
  window.addEventListener("pageshow", applyBottomOffset);
  document.addEventListener("visibilitychange", applyBottomOffset);
})();
`;

// Installed once the New Relic agent has loaded (so our wrapper sits in front of
// the agent's console instrumentation). It drops one benign Next.js message so
// it isn't forwarded to New Relic:
//   "Failed to fetch RSC payload for <url>. Falling back to browser navigation."
// Next.js logs this when a client-side navigation can't fetch the RSC payload
// (a transient network/CDN hiccup in the WebView) and then recovers on its own
// with a full browser navigation — so it's noise, not an actionable error.
function filterNewRelicRscNoise() {
  if (typeof window === "undefined") return;
  const w = window as typeof window & { __ngRscFilterInstalled?: boolean };
  if (w.__ngRscFilterInstalled) return;
  w.__ngRscFilterInstalled = true;

  const original = console.error;
  console.error = (...args: unknown[]) => {
    const first = args[0];
    if (
      typeof first === "string" &&
      first.includes("Failed to fetch RSC payload")
    ) {
      return;
    }
    original(...args);
  };
}

function ConditionalGTM() {
  const pathname = usePathname();

  // Don't render GTM in preview mode
  if (pathname?.startsWith("/preview")) {
    return null;
  }

  return <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID || ""} />;
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const loadNewRelic = process.env.NEXT_PUBLIC_LOAD_NEW_RELIC === "true";

  return (
    // "ja", not "jp" — "jp" is a country code, not a language tag, so the page
    // never declared a language WebKit could act on when picking a CJK face.
    // jp-amk (Welcia prod) declares "ja" here too.
    <html lang="ja">
      <head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <script
          id="native-webview-bottom-offset"
          dangerouslySetInnerHTML={{ __html: nativeWebViewBottomOffsetScript }}
        />
        {loadNewRelic && (
          <Script
            id="new-relic"
            strategy="lazyOnload" // load after page is interactive
            src={process.env.NEXT_PUBLIC_NEW_RELIC_SCRIPT_URL}
            onReady={filterNewRelicRscNoise}
          ></Script>
        )}
        <link
          rel="icon"
          href="/icons/favicon.png"
          type="image/png"
          sizes="any"
        />
      </head>
      <ConditionalGTM />
      <body suppressHydrationWarning data-testid="app-root">
        <OpenAPIProvider>
          <ReactQueryProvider>
            <ZustandProvider>{children}</ZustandProvider>
          </ReactQueryProvider>
        </OpenAPIProvider>
      </body>
    </html>
  );
}
